import { functionValidationRules, predefinedCustomFormulaFunctions } from "./customFormulaFunctions";
import {
  escapeFormulaQuotedValue,
  findUnescapedQuoteForward,
  unescapeFormulaQuotedValue,
} from "./customFormulaQuotedValue";
export { predefinedCustomFormulaFunctions } from "./customFormulaFunctions";

export interface FormulaReferenceField {
  guid: string;
  caption: string;
}

interface FormulaTokenBase {
  kind: "field" | "function" | "number" | "operator" | "openParen" | "closeParen" | "comma";
}

export interface FieldFormulaToken extends FormulaTokenBase {
  kind: "field";
  guid: string;
  caption: string;
}

export interface FunctionFormulaToken extends FormulaTokenBase {
  kind: "function";
  name: string;
}

export interface NumberFormulaToken extends FormulaTokenBase {
  kind: "number";
  value: string;
}

export interface OperatorFormulaToken extends FormulaTokenBase {
  kind: "operator";
  value: "+" | "-" | "*" | "/";
}

export interface OpenParenFormulaToken extends FormulaTokenBase {
  kind: "openParen";
}

export interface CloseParenFormulaToken extends FormulaTokenBase {
  kind: "closeParen";
}

export interface CommaFormulaToken extends FormulaTokenBase {
  kind: "comma";
}

export type FormulaToken =
  | FieldFormulaToken
  | FunctionFormulaToken
  | NumberFormulaToken
  | OperatorFormulaToken
  | OpenParenFormulaToken
  | CloseParenFormulaToken
  | CommaFormulaToken;

interface LexicalToken {
  kind: "quoted" | "identifier" | "number" | "operator" | "openParen" | "closeParen" | "comma";
  value: string;
  index: number;
}

export interface ParsedCustomFormula {
  tokens: FormulaToken[];
  canonicalFormula: string;
  displayFormula: string;
  formulaParameters: string[];
}

export interface ParseCustomFormulaResult {
  isValid: boolean;
  parsed?: ParsedCustomFormula;
  error?: string;
}

const isWhitespaceCharacter = (value: string): boolean => {
  return /\s/.test(value);
};

const isOperatorCharacter = (value: string): value is "+" | "-" | "*" | "/" => {
  return value === "+" || value === "-" || value === "*" || value === "/";
};

const isNumberStartCharacter = (value: string): boolean => {
  return /[0-9]/.test(value);
};

const isIdentifierStartCharacter = (value: string): boolean => {
  return /[A-Za-z_]/.test(value);
};

const isIdentifierCharacter = (value: string): boolean => {
  return /[A-Za-z0-9_]/.test(value);
};

const readQuotedToken = (
  input: string,
  startIndex: number
): { token?: LexicalToken; nextIndex: number; error?: string } => {
  const quoteEndIndex = findUnescapedQuoteForward(input, startIndex + 1);
  if (quoteEndIndex < 0) {
    return {
      nextIndex: input.length,
      error: `Missing closing quote — a quoted name starting at position ${startIndex + 1} is never closed`,
    };
  }

  const rawValue = input.slice(startIndex + 1, quoteEndIndex);
  const unescapeResult = unescapeFormulaQuotedValue(rawValue);
  if (unescapeResult.errorIndex !== undefined) {
    return {
      nextIndex: quoteEndIndex + 1,
      error: `Invalid special character sequence at position ${startIndex + unescapeResult.errorIndex + 2}. Use \\" to include a quote inside a name`,
    };
  }

  return {
    token: { kind: "quoted", value: unescapeResult.value ?? "", index: startIndex },
    nextIndex: quoteEndIndex + 1,
  };
};

const readNumberToken = (input: string, startIndex: number): { token: LexicalToken; nextIndex: number } => {
  let cursor = startIndex;
  let value = "";
  let dotUsed = false;

  while (cursor < input.length) {
    const currentCharacter = input[cursor];
    if (currentCharacter === undefined) {
      break;
    }
    if (/[0-9]/.test(currentCharacter)) {
      value += currentCharacter;
      cursor++;
      continue;
    }
    if (currentCharacter === "." && !dotUsed) {
      dotUsed = true;
      value += currentCharacter;
      cursor++;
      continue;
    }
    break;
  }

  return {
    token: { kind: "number", value, index: startIndex },
    nextIndex: cursor,
  };
};

const readIdentifierToken = (input: string, startIndex: number): { token: LexicalToken; nextIndex: number } => {
  let cursor = startIndex;
  let value = "";

  while (cursor < input.length) {
    const currentCharacter = input[cursor];
    if (currentCharacter === undefined || !isIdentifierCharacter(currentCharacter)) {
      break;
    }
    value += currentCharacter;
    cursor++;
  }

  return {
    token: { kind: "identifier", value, index: startIndex },
    nextIndex: cursor,
  };
};

const toSingleCharacterToken = (value: string, index: number): LexicalToken | undefined => {
  if (isOperatorCharacter(value)) {
    return { kind: "operator", value, index };
  }
  if (value === "(") {
    return { kind: "openParen", value, index };
  }
  if (value === ")") {
    return { kind: "closeParen", value, index };
  }
  if (value === ",") {
    return { kind: "comma", value, index };
  }
  return undefined;
};

const readNextLexicalToken = (
  input: string,
  index: number
): { token?: LexicalToken; nextIndex: number; error?: string } => {
  const character = input[index];
  if (character === undefined) {
    return { nextIndex: index + 1 };
  }

  if (isWhitespaceCharacter(character)) {
    return { nextIndex: index + 1 };
  }

  const singleCharacterToken = toSingleCharacterToken(character, index);
  if (singleCharacterToken !== undefined) {
    return { token: singleCharacterToken, nextIndex: index + 1 };
  }

  if (character === '"') {
    return readQuotedToken(input, index);
  }

  if (isNumberStartCharacter(character) || (character === "." && isNumberStartCharacter(input[index + 1] ?? ""))) {
    return readNumberToken(input, index);
  }

  if (isIdentifierStartCharacter(character)) {
    return readIdentifierToken(input, index);
  }

  if (character === "{" || character === "}") {
    return {
      nextIndex: index + 1,
      error: "Curly braces are reserved for system placeholders. Use field names in quotes.",
    };
  }

  return {
    nextIndex: index + 1,
    error: `Unexpected character "${character}" at position ${index + 1}. Only numbers, field names in quotes, operators (+, -, *, /), and parentheses are allowed`,
  };
};

const tokenizeFormula = (input: string): { tokens?: LexicalToken[]; error?: string } => {
  const tokens: LexicalToken[] = [];
  let index = 0;

  while (index < input.length) {
    const readResult = readNextLexicalToken(input, index);
    if (readResult.error) {
      return { error: readResult.error };
    }
    if (readResult.token !== undefined) {
      tokens.push(readResult.token);
    }
    index = readResult.nextIndex;
  }

  return { tokens };
};

const buildCaptionLookup = (fields: FormulaReferenceField[]): Map<string, FormulaReferenceField[]> => {
  const lookup = new Map<string, FormulaReferenceField[]>();
  fields.forEach((field) => {
    const key = field.caption.toLowerCase();
    const existing = lookup.get(key);
    if (existing === undefined) {
      lookup.set(key, [field]);
      return;
    }
    existing.push(field);
  });

  return lookup;
};

const buildGuidLookup = (fields: FormulaReferenceField[]): Map<string, FormulaReferenceField> => {
  const lookup = new Map<string, FormulaReferenceField>();
  fields.forEach((field) => {
    lookup.set(field.guid.toLowerCase(), field);
  });
  return lookup;
};

const buildFunctionLookup = (functions: string[]): Map<string, string> => {
  const lookup = new Map<string, string>();
  functions.forEach((fn) => {
    lookup.set(fn.toLowerCase(), fn);
  });
  return lookup;
};

const resolveField = (
  value: string,
  guidLookup: Map<string, FormulaReferenceField>,
  captionLookup: Map<string, FormulaReferenceField[]>
): { field?: FormulaReferenceField; error?: string } => {
  const guidMatch = guidLookup.get(value.toLowerCase());
  if (guidMatch !== undefined) {
    return { field: guidMatch };
  }

  const captionMatches = captionLookup.get(value.toLowerCase());
  if (captionMatches === undefined || captionMatches.length === 0) {
    return { error: `Field "${value}" not found. Make sure the field name is spelled correctly` };
  }

  if (captionMatches.length > 1) {
    return { error: `Multiple fields share the name "${value}". Use a unique field name to avoid ambiguity` };
  }

  return { field: captionMatches[0] };
};

const toSimpleSemanticToken = (token: LexicalToken): FormulaToken | undefined => {
  if (token.kind === "operator") {
    return { kind: "operator", value: token.value as "+" | "-" | "*" | "/" };
  }
  if (token.kind === "openParen") {
    return { kind: "openParen" };
  }
  if (token.kind === "closeParen") {
    return { kind: "closeParen" };
  }
  if (token.kind === "comma") {
    return { kind: "comma" };
  }
  if (token.kind === "number") {
    const value = token.value.startsWith(".") ? `0${token.value}` : token.value.replace(/^0+(?=\d)/, "");
    return { kind: "number", value };
  }
  return undefined;
};

const tryResolveFunctionToken = (
  token: LexicalToken,
  functionLookup: Map<string, string>
): FunctionFormulaToken | undefined => {
  if (token.kind !== "identifier") {
    return undefined;
  }
  const functionName = functionLookup.get(token.value.toLowerCase());
  if (functionName === undefined) {
    return undefined;
  }
  return { kind: "function", name: functionName };
};

const resolveFieldToken = (
  token: LexicalToken,
  guidLookup: Map<string, FormulaReferenceField>,
  captionLookup: Map<string, FormulaReferenceField[]>
): { token?: FieldFormulaToken; error?: string } => {
  const resolution = resolveField(token.value, guidLookup, captionLookup);
  if (resolution.error) {
    return { error: resolution.error };
  }

  const field = resolution.field;
  if (field === undefined) {
    return { error: `Field "${token.value}" not found. Make sure the field name is spelled correctly` };
  }

  return {
    token: {
      kind: "field",
      guid: field.guid,
      caption: field.caption,
    },
  };
};

const toSemanticTokens = (
  lexicalTokens: LexicalToken[],
  fields: FormulaReferenceField[],
  functions: string[]
): { tokens?: FormulaToken[]; error?: string } => {
  const guidLookup = buildGuidLookup(fields);
  const captionLookup = buildCaptionLookup(fields);
  const functionLookup = buildFunctionLookup(functions);
  const tokens: FormulaToken[] = [];

  for (const lexicalToken of lexicalTokens) {
    const simpleToken = toSimpleSemanticToken(lexicalToken);
    if (simpleToken !== undefined) {
      tokens.push(simpleToken);
      continue;
    }

    const functionToken = tryResolveFunctionToken(lexicalToken, functionLookup);
    if (functionToken !== undefined) {
      tokens.push(functionToken);
      continue;
    }

    const fieldTokenResult = resolveFieldToken(lexicalToken, guidLookup, captionLookup);
    if (fieldTokenResult.error) {
      return { error: fieldTokenResult.error };
    }
    if (fieldTokenResult.token === undefined) {
      return { error: `Unable to resolve value "${lexicalToken.value}"` };
    }
    tokens.push(fieldTokenResult.token);
  }

  return { tokens };
};

const isOperandToken = (token: FormulaToken): boolean => {
  return token.kind === "field" || token.kind === "number" || token.kind === "function";
};

const validateFirstToken = (token: FormulaToken): string | undefined => {
  if (token.kind === "operator" && token.value !== "+" && token.value !== "-") {
    return "Formula cannot start with * or /. Use + or - if you need a sign";
  }
  if (token.kind === "comma" || token.kind === "closeParen") {
    return "Formula must start with a field name, number, or opening parenthesis";
  }
  return undefined;
};

const validateCurrentParenthesisState = (token: FormulaToken, openParentheses: number): string | undefined => {
  if (token.kind !== "closeParen") {
    return undefined;
  }
  if (openParentheses < 0) {
    return 'Extra closing parenthesis ")" without a matching opening parenthesis';
  }
  return undefined;
};

const isOperandOrCloseParen = (token: FormulaToken | undefined): boolean => {
  if (token === undefined) {
    return false;
  }

  return isOperandToken(token) || token.kind === "closeParen";
};

const isUnaryMinusToken = (
  token: FormulaToken,
  tokenBefore: FormulaToken | undefined
): token is OperatorFormulaToken => {
  if (token.kind !== "operator" || token.value !== "-") {
    return false;
  }

  return !isOperandOrCloseParen(tokenBefore);
};

const validateAdjacentTokens = (
  previousPreviousToken: FormulaToken | undefined,
  previousToken: FormulaToken,
  token: FormulaToken
): string | undefined => {
  const previousIsOperand = isOperandToken(previousToken) || previousToken.kind === "closeParen";
  const currentIsOperand = isOperandToken(token) || token.kind === "openParen";

  if (previousIsOperand && currentIsOperand && previousToken.kind !== "function") {
    return "Missing operator between values";
  }

  const previousNeedsOperand =
    previousToken.kind === "operator" || previousToken.kind === "comma" || previousToken.kind === "openParen";
  const currentBreaksOperand = token.kind === "operator" || token.kind === "comma" || token.kind === "closeParen";
  if (previousNeedsOperand && currentBreaksOperand) {
    if (token.kind === "operator" && token.value === "-") {
      if (isUnaryMinusToken(previousToken, previousPreviousToken)) {
        return "Unexpected element in formula. Check for extra or misplaced operators, commas, or parentheses";
      }

      return undefined;
    }
    return "Unexpected element in formula. Check for extra or misplaced operators, commas, or parentheses";
  }

  if (previousToken.kind === "function" && token.kind !== "openParen") {
    return `Function "${previousToken.name}" must be followed by "("`;
  }

  return undefined;
};

const isInvalidEndingToken = (token: FormulaToken | undefined): boolean => {
  if (token === undefined) {
    return false;
  }
  return token.kind === "operator" || token.kind === "comma" || token.kind === "openParen" || token.kind === "function";
};

const validateSyntax = (tokens: FormulaToken[]): string | undefined => {
  if (tokens.length === 0) {
    return "Formula cannot be empty";
  }

  let openParentheses = 0;
  let previousToken: FormulaToken | undefined = undefined;
  let previousPreviousToken: FormulaToken | undefined = undefined;

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    if (token === undefined) {
      continue;
    }

    if (token.kind === "openParen") {
      openParentheses++;
    } else if (token.kind === "closeParen") {
      openParentheses--;
    }

    const parenthesisError = validateCurrentParenthesisState(token, openParentheses);
    if (parenthesisError) {
      return parenthesisError;
    }

    if (previousToken === undefined) {
      const firstTokenError = validateFirstToken(token);
      if (firstTokenError) {
        return firstTokenError;
      }
      previousToken = token;
      continue;
    }

    const adjacentTokensError = validateAdjacentTokens(previousPreviousToken, previousToken, token);
    if (adjacentTokensError) {
      return adjacentTokensError;
    }

    previousPreviousToken = previousToken;
    previousToken = token;
  }

  if (openParentheses !== 0) {
    return 'Mismatched parentheses — every opening "(" must have a matching closing ")"';
  }

  const lastToken = tokens[tokens.length - 1];
  if (isInvalidEndingToken(lastToken)) {
    return "Formula is incomplete — it cannot end with an operator, comma, or opening parenthesis";
  }

  return undefined;
};

const findClosingParenthesisIndex = (tokens: FormulaToken[], openingParenthesisIndex: number): number | undefined => {
  let nestedParenthesesCount = 0;

  for (let i = openingParenthesisIndex; i < tokens.length; i++) {
    const token = tokens[i];
    if (!token) {
      continue;
    }

    if (token.kind === "openParen") {
      nestedParenthesesCount++;
    } else if (token.kind === "closeParen") {
      nestedParenthesesCount--;
      if (nestedParenthesesCount === 0) {
        return i;
      }
    }
  }

  return undefined;
};

const splitFunctionArgumentTokens = (
  tokens: FormulaToken[],
  argumentStartIndex: number,
  argumentEndIndex: number
): FormulaToken[][] => {
  if (argumentStartIndex > argumentEndIndex) {
    return [];
  }

  const argumentsTokens: FormulaToken[][] = [];
  let currentArgumentTokens: FormulaToken[] = [];
  let nestedParenthesesCount = 0;

  for (let i = argumentStartIndex; i <= argumentEndIndex; i++) {
    const token = tokens[i];
    if (!token) {
      continue;
    }

    if (token.kind === "comma" && nestedParenthesesCount === 0) {
      argumentsTokens.push(currentArgumentTokens);
      currentArgumentTokens = [];
      continue;
    }

    if (token.kind === "openParen") {
      nestedParenthesesCount++;
    } else if (token.kind === "closeParen") {
      nestedParenthesesCount--;
    }

    currentArgumentTokens.push(token);
  }

  argumentsTokens.push(currentArgumentTokens);
  return argumentsTokens;
};

const validateFunctionCalls = (tokens: FormulaToken[]): string | undefined => {
  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    if (!token || token.kind !== "function") {
      continue;
    }

    const functionRule = functionValidationRules[token.name.toLowerCase()];
    if (!functionRule) {
      continue;
    }

    const openingParenthesisToken = tokens[i + 1];
    if (!openingParenthesisToken || openingParenthesisToken.kind !== "openParen") {
      continue;
    }

    const closingParenthesisIndex = findClosingParenthesisIndex(tokens, i + 1);
    if (closingParenthesisIndex === undefined) {
      return 'Mismatched parentheses — every opening "(" must have a matching closing ")"';
    }

    const argumentsTokens = splitFunctionArgumentTokens(tokens, i + 2, closingParenthesisIndex - 1);
    const functionArgumentsError = functionRule.validateArguments(token, argumentsTokens);
    if (functionArgumentsError) {
      return functionArgumentsError;
    }
  }

  return undefined;
};

const serializeFormula = (tokens: FormulaToken[], mode: "display" | "canonical"): string => {
  let result = "";
  let previousToken: FormulaToken | undefined = undefined;

  tokens.forEach((token) => {
    const appendWithOptionalSpace = (value: string): void => {
      if (
        result.length > 0 &&
        previousToken &&
        previousToken.kind !== "openParen" &&
        previousToken.kind !== "operator" &&
        previousToken.kind !== "comma"
      ) {
        result += " ";
      }
      result += value;
    };

    if (token.kind === "field") {
      const fieldValue = mode === "display" ? token.caption : token.guid;
      appendWithOptionalSpace(`"${escapeFormulaQuotedValue(fieldValue)}"`);
      previousToken = token;
      return;
    }

    if (token.kind === "function") {
      appendWithOptionalSpace(token.name);
      previousToken = token;
      return;
    }

    if (token.kind === "number") {
      appendWithOptionalSpace(token.value);
      previousToken = token;
      return;
    }

    if (token.kind === "operator") {
      result = result.trimEnd();
      result += ` ${token.value} `;
      previousToken = token;
      return;
    }

    if (token.kind === "openParen") {
      result = result.trimEnd();
      result += "(";
      previousToken = token;
      return;
    }

    if (token.kind === "closeParen") {
      result = result.trimEnd();
      result += ")";
      previousToken = token;
      return;
    }

    if (token.kind === "comma") {
      result = result.trimEnd();
      result += ", ";
      previousToken = token;
    }
  });

  return result.trim();
};

const extractFormulaParameters = (tokens: FormulaToken[]): string[] => {
  const parameters: string[] = [];
  const visited = new Set<string>();

  tokens.forEach((token) => {
    if (token.kind !== "field") {
      return;
    }
    if (visited.has(token.guid)) {
      return;
    }
    visited.add(token.guid);
    parameters.push(token.guid);
  });

  return parameters;
};

const validateContainsFieldReference = (tokens: FormulaToken[]): string | undefined => {
  const hasFieldReference = tokens.some((token) => token.kind === "field");
  if (!hasFieldReference) {
    return "Add at least one column to your formula";
  }

  return undefined;
};

export const parseCustomFormula = (
  input: string,
  fields: FormulaReferenceField[],
  functions: string[] = predefinedCustomFormulaFunctions
): ParseCustomFormulaResult => {
  const lexicalResult = tokenizeFormula(input);
  if (lexicalResult.error) {
    return { isValid: false, error: lexicalResult.error };
  }

  const lexicalTokens = lexicalResult.tokens ?? [];
  const semanticResult = toSemanticTokens(lexicalTokens, fields, functions);
  if (semanticResult.error) {
    return { isValid: false, error: semanticResult.error };
  }

  const semanticTokens = semanticResult.tokens ?? [];
  const syntaxError = validateSyntax(semanticTokens);
  if (syntaxError) {
    return { isValid: false, error: syntaxError };
  }

  const functionCallError = validateFunctionCalls(semanticTokens);
  if (functionCallError) {
    return { isValid: false, error: functionCallError };
  }

  const fieldReferenceError = validateContainsFieldReference(semanticTokens);
  if (fieldReferenceError) {
    return { isValid: false, error: fieldReferenceError };
  }

  return {
    isValid: true,
    parsed: {
      tokens: semanticTokens,
      canonicalFormula: serializeFormula(semanticTokens, "canonical"),
      displayFormula: serializeFormula(semanticTokens, "display"),
      formulaParameters: extractFormulaParameters(semanticTokens),
    },
  };
};
